<template>
  <UiModalShell
    :show="props.showModal"
    :title="modalTitle"
    :subtitle="modalSubtitle"
    size="lg"
    @close="closeModal"
  >
    <!-- Body: CUSTOMER (MCS) branch — P7 T6, restyled to Console tokens -->
    <div v-if="isCustomerAccount">
      <!-- Error Message (MCS) -->
      <div
        v-if="mcsError"
        class="mb-4 p-3 bg-[rgba(240,82,82,0.08)] border border-[rgba(240,82,82,0.35)] rounded-[var(--radius-md)]"
      >
        <p class="text-err text-[13px]">{{ mcsError }}</p>
      </div>

      <!-- Manual add sub-modal -->
      <ManualAddCustomerForm
        :showModal="showManualAdd"
        :preselectedSiteId="selectedSiteChip"
        @close="showManualAdd = false"
        @added="handleManualAdded"
      ></ManualAddCustomerForm>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <!-- LEFT: Available -->
        <div class="space-y-3">
          <h4 class="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3">
            Available
          </h4>

          <!-- Site chips -->
          <div class="flex flex-wrap gap-1.5">
            <button
              type="button"
              @click="setSiteChip(null)"
              :class="[
                'font-mono text-[11px] uppercase tracking-wide px-2 py-1 rounded border transition-colors',
                selectedSiteChip === null
                  ? 'bg-[rgba(184,230,0,0.12)] text-lime border-[rgba(184,230,0,0.5)]'
                  : 'bg-bg-1 text-text-3 border-line-2 hover:text-text hover:border-line',
              ]"
            >
              All sites
            </button>
            <button
              v-for="site in sites"
              :key="site.id"
              type="button"
              @click="setSiteChip(site.id)"
              :class="[
                'font-mono text-[11px] uppercase tracking-wide px-2 py-1 rounded border transition-colors',
                selectedSiteChip === site.id
                  ? 'bg-[rgba(184,230,0,0.12)] text-lime border-[rgba(184,230,0,0.5)]'
                  : 'bg-bg-1 text-text-3 border-line-2 hover:text-text hover:border-line',
              ]"
            >
              {{ site.display_name }}
            </button>
          </div>

          <!-- Search -->
          <UiInput
            v-model="mcsSearchInput"
            :left-icon="Search"
            clearable
            placeholder="Search by customer name or number…"
          />

          <!-- Manually add link -->
          <div>
            <button
              type="button"
              @click="showManualAdd = true"
              class="font-mono text-[11px] text-lime hover:underline"
            >
              + Manually add customer
            </button>
          </div>

          <!-- Results list -->
          <div class="border border-line rounded-[var(--radius-md)] overflow-hidden">
            <div class="max-h-[320px] overflow-y-auto">
              <div v-if="mcsLoadingList" class="px-4 py-8 text-center">
                <Loader2 :size="20" class="inline-block animate-spin text-lime" />
                <p class="mt-2 text-text-3 text-[12.5px]">Loading…</p>
              </div>
              <template v-else>
                <UiTable
                  v-if="mcsAvailableResults.length"
                  :columns="[
                    { key: 'site', label: 'Site' },
                    { key: 'cust_no', label: 'Cust No' },
                    { key: 'name', label: 'Name' },
                    { key: 'state', label: '' },
                  ]"
                >
                  <tr
                    v-for="c in mcsAvailableResults"
                    :key="c.id"
                    :class="[
                      isAssigned(c.id)
                        ? 'cursor-default opacity-70'
                        : 'cursor-pointer',
                    ]"
                    @click="addMcsAssignment(c)"
                  >
                    <td>
                      <UiBadge tone="lime">{{ c.site_name }}</UiBadge>
                    </td>
                    <td class="mono">{{ c.customer_no }}</td>
                    <td>{{ c.customer_name }}</td>
                    <td class="text-right">
                      <UiStatusPill v-if="isAssigned(c.id)" status="ok">
                        assigned
                      </UiStatusPill>
                      <Plus v-else :size="14" class="inline-block text-lime" />
                    </td>
                  </tr>
                </UiTable>
                <UiEmpty
                  v-else
                  :icon="SearchX"
                  :title="mcsSearchInput || selectedSiteChip !== null
                    ? 'No matching customers found'
                    : 'Start typing to search, or pick a site chip'"
                />
              </template>
            </div>
          </div>
        </div>

        <!-- RIGHT: Assigned -->
        <div class="space-y-3">
          <h4 class="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3">
            Assigned ({{ mcsAssigned.length }})
          </h4>

          <UiEmpty
            v-if="!mcsAssigned.length"
            :icon="Inbox"
            title="No MCS customers assigned yet"
            hint="Pick a row on the left to add one."
          />

          <div v-else class="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            <div
              v-for="group in mcsAssignedGrouped"
              :key="group.site"
              class="border border-line rounded-[var(--radius-md)] p-3 bg-bg"
            >
              <div class="flex items-center justify-between mb-2">
                <UiBadge tone="lime">{{ group.site }}</UiBadge>
                <span class="font-mono text-[11px] text-text-3">
                  {{ group.items.length }}
                </span>
              </div>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="c in group.items"
                  :key="c.mcs_customer_id"
                  class="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-line-2 bg-bg-1 text-text-2 text-[12px]"
                >
                  <span class="font-mono text-[11px] text-text-3">{{ c.customer_no }}</span>
                  <span>{{ c.customer_name }}</span>
                  <button
                    type="button"
                    @click="removeMcsAssignment(c)"
                    class="text-text-3 hover:text-err transition-colors"
                    title="Remove"
                  >
                    <X :size="12" />
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <!-- Body: SUPPLIER (legacy group/site) branch — preserved from pre-P7 T6 -->
      <div v-else class="p-6">
        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
        >
          <p class="text-red-600 text-sm">{{ error }}</p>
        </div>

        <!-- Current Assignments -->
        <div class="mb-6">
          <h4 class="text-sm font-medium text-gray-700 mb-2">
            Current {{ isCustomerAccount ? 'Customers' : 'Groups' }} ({{ currentAssignments.length }})
          </h4>
          <div v-if="currentAssignments.length" class="flex flex-wrap gap-2">
            <span
              v-for="item in currentAssignments"
              :key="item.id"
              class="px-3 py-1 rounded-full text-sm flex items-center gap-2"
              :class="isCustomerAccount ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'"
            >
              <span>{{ item.name }}</span>
              <span class="text-xs opacity-70">({{ item.subtext }})</span>
              <button
                @click="removeAssignment(item)"
                class="ml-1 hover:text-red-600 transition-colors"
                title="Remove"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </span>
          </div>
          <p v-else class="text-gray-500 text-sm italic">
            No {{ isCustomerAccount ? 'customers' : 'groups' }} assigned
          </p>
        </div>

        <!-- Available Items -->
        <div>
          <h4 class="text-sm font-medium text-gray-700 mb-2">
            Available {{ isCustomerAccount ? 'Customers' : 'Groups' }}
          </h4>

          <!-- Search -->
          <div class="relative mb-3">
            <input
              type="text"
              v-model="searchQuery"
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              :class="isCustomerAccount ? 'focus:ring-green-500' : 'focus:ring-purple-500'"
              :placeholder="`Search ${isCustomerAccount ? 'customers' : 'groups'}...`"
            />
            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>

          <!-- Site Filter (for groups) -->
          <div v-if="!isCustomerAccount" class="mb-3">
            <select
              v-model="selectedSite"
              @change="fetchGroupsForSite"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="FA669BD0-42F8-4EDF-8847-9ACD191759F1">Dubai</option>
              <option value="A2CB2A29-A085-4DD5-B33E-E1C50F0BE92E">Abu Dhabi</option>
            </select>
          </div>

          <!-- List -->
          <div class="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            <div v-if="loadingItems" class="px-4 py-8 text-center text-gray-500">
              <svg
                class="animate-spin h-6 w-6 mx-auto mb-2"
                :class="isCustomerAccount ? 'text-green-600' : 'text-purple-600'"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
              </svg>
              Loading...
            </div>
            <div
              v-else-if="filteredAvailableItems.length"
              v-for="item in filteredAvailableItems"
              :key="item.id"
              @click="addAssignment(item)"
              class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center justify-between transition-colors"
            >
              <div>
                <p class="text-sm font-medium text-gray-900">{{ item.name }}</p>
                <p class="text-xs text-gray-500">{{ item.subtext }}</p>
              </div>
              <i
                class="fas fa-plus"
                :class="isCustomerAccount ? 'text-green-400' : 'text-purple-400'"
              ></i>
            </div>
            <div
              v-else-if="!loadingItems"
              class="px-4 py-8 text-center text-gray-500"
            >
              <i class="fas fa-search text-2xl mb-2 opacity-50"></i>
              <p class="text-sm">
                {{ searchQuery ? 'No matching items found' : `All ${isCustomerAccount ? 'customers' : 'groups'} have been assigned` }}
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-6 flex gap-3">
          <button
            type="button"
            @click="closeModal"
            :disabled="loading"
            class="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="saveAssignments"
            :disabled="loading"
            :class="isCustomerAccount ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'"
            class="flex-1 px-6 py-2 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
          >
            Save Assignments
          </button>
        </div>
      </div>

    <template #footer>
      <UiButton
        variant="ghost"
        :disabled="isCustomerAccount ? mcsSaving : loading"
        @click="closeModal"
      >
        Cancel
      </UiButton>
      <UiButton
        variant="primary"
        :loading="isCustomerAccount ? mcsSaving : loading"
        :disabled="isCustomerAccount ? mcsSaving : loading"
        @click="onFooterSave"
      >
        Save Assignments
      </UiButton>
    </template>
  </UiModalShell>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { AdminApiService } from '@/services/adminApi';
import ManualAddCustomerForm from '@/components/ManualAddCustomerForm.vue';
import UiModalShell from '@/components/ui/UiModalShell.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiTable from '@/components/ui/UiTable.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiStatusPill from '@/components/ui/UiStatusPill.vue';
import UiEmpty from '@/components/ui/UiEmpty.vue';
import { Search, SearchX, Plus, Inbox, Loader2, X } from 'lucide-vue-next';

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  },
  user: {
    type: Object,
    default: null
  },
  availableCustomers: {
    type: Array,
    default: () => []
  },
  availableGroups: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'saveCustomers', 'saveGroups', 'fetchGroups', 'saved']);

// --- Legacy (SUPPLIER) state — unchanged from pre-P7 T6 ---
const loading = ref(false);
const loadingItems = ref(false);
const error = ref(null);
const searchQuery = ref('');
const currentAssignments = ref([]);
const selectedSite = ref('FA669BD0-42F8-4EDF-8847-9ACD191759F1'); // Default to Dubai

const isCustomerAccount = computed(() => props.user?.account_type === 'CUSTOMER');

const modalTitle = computed(() =>
  isCustomerAccount.value
    ? 'Manage Customer Assignments'
    : 'Manage Group/Supplier Assignments'
);

const modalSubtitle = computed(() => {
  if (!props.user) return '';
  const fn = props.user.first_name || '';
  const ln = props.user.last_name || '';
  const em = props.user.email || '';
  return `${fn} ${ln} (${em})`.trim();
});

// --- MCS (CUSTOMER) state — new in P7 T6 ---
const store = useStore();
const adminApi = new AdminApiService(store.state.apiUrl);

const mcsAssigned = ref([]); // [{mcs_customer_id, site, company_id, customer_no, customer_name}]
const mcsAvailableResults = ref([]);
const mcsLoadingList = ref(false);
const mcsSaving = ref(false);
const mcsError = ref(null);
const mcsSearchInput = ref('');
const mcsSearchQuery = ref(''); // debounced
const sites = ref([]);
const selectedSiteChip = ref(null); // null === "All sites"
const showManualAdd = ref(false);
let mcsSearchTimeoutId = null;
let initialAssignedIds = []; // snapshot taken on modal open, used to compute "new" count on save

// Initialize current assignments when user changes or modal opens
watch(
  () => [props.user, props.showModal],
  ([newUser, isOpen]) => {
    if (newUser && isOpen) {
      initializeAssignments(newUser);
      searchQuery.value = '';
      error.value = null;

      // MCS branch init
      if (newUser.account_type === 'CUSTOMER') {
        mcsError.value = null;
        mcsSearchInput.value = '';
        mcsSearchQuery.value = '';
        selectedSiteChip.value = null;
        mcsAssigned.value = normalizeCustomersMcs(newUser.customers_mcs);
        initialAssignedIds = mcsAssigned.value.map((c) => c.mcs_customer_id);
        fetchSites();
        fetchMcsAvailable();
      }
    }
  },
  { immediate: true }
);

// Debounce search typing → mcsSearchQuery → fetch
watch(mcsSearchInput, (v) => {
  if (mcsSearchTimeoutId) clearTimeout(mcsSearchTimeoutId);
  mcsSearchTimeoutId = setTimeout(() => {
    mcsSearchQuery.value = v;
    fetchMcsAvailable();
  }, 300);
});
onBeforeUnmount(() => {
  if (mcsSearchTimeoutId) clearTimeout(mcsSearchTimeoutId);
});

function normalizeCustomersMcs(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((e) => ({
      mcs_customer_id: e.mcs_customer_id,
      site: e.site || e.site_name || '',
      company_id: e.company_id,
      customer_no: e.customer_no,
      customer_name: e.customer_name,
    }))
    .filter((e) => e.mcs_customer_id != null);
}

const initializeAssignments = (user) => {
  if (user.account_type === 'CUSTOMER') {
    currentAssignments.value = (user.customers || []).map((c) => ({
      id: c.customer_id,
      name: c.customer_name,
      subtext: c.site_name,
      original: c
    }));
  } else {
    currentAssignments.value = (user.group || []).map((g) => ({
      id: g.group_id,
      name: g.group_name,
      subtext: g.site_name,
      original: g
    }));
  }
};

// Compute available items (excluding already assigned) — LEGACY, SUPPLIER branch only
const availableItems = computed(() => {
  const assignedIds = new Set(currentAssignments.value.map((a) => a.id));

  if (isCustomerAccount.value) {
    return (props.availableCustomers || [])
      .filter((c) => !assignedIds.has(c.CustomerID))
      .map((c) => ({
        id: c.CustomerID,
        name: c.CustomerName,
        subtext: c.SiteName,
        original: c
      }));
  } else {
    return (props.availableGroups || [])
      .filter((g) => !assignedIds.has(g.GroupId))
      .map((g) => ({
        id: g.GroupId,
        name: g.GroupName,
        subtext: g.SiteName || selectedSite.value === 'FA669BD0-42F8-4EDF-8847-9ACD191759F1' ? 'Dubai' : 'Abu Dhabi',
        original: g
      }));
  }
});

// Filter available items by search query — LEGACY
const filteredAvailableItems = computed(() => {
  if (!searchQuery.value) return availableItems.value;
  const query = searchQuery.value.toLowerCase();
  return availableItems.value.filter(
    (item) =>
      item.name.toLowerCase().includes(query) ||
      item.subtext?.toLowerCase().includes(query)
  );
});

const addAssignment = (item) => {
  // Check if already assigned
  if (!currentAssignments.value.find((a) => a.id === item.id)) {
    currentAssignments.value.push({ ...item });
  }
};

const removeAssignment = (item) => {
  currentAssignments.value = currentAssignments.value.filter(
    (a) => a.id !== item.id
  );
};

const fetchGroupsForSite = () => {
  emit('fetchGroups', selectedSite.value);
};

const closeModal = () => {
  if (mcsSaving.value || loading.value) return;
  searchQuery.value = '';
  error.value = null;
  mcsError.value = null;
  emit('close');
};

const saveAssignments = () => {
  error.value = null;

  if (isCustomerAccount.value) {
    const customers = currentAssignments.value.map((a) => ({
      customer_id: a.id,
      customer_name: a.name,
      site_name: a.subtext
    }));
    emit('saveCustomers', { user_id: props.user.id, customers });
  } else {
    const groups = currentAssignments.value.map((a) => ({
      group_id: a.id,
      group_name: a.name,
      site_name: a.subtext
    }));
    emit('saveGroups', { user_id: props.user.id, group: groups });
  }
};

// Footer dispatcher — CUSTOMER → MCS save (emits `saved`),
// SUPPLIER → legacy save (emits `saveCustomers` / `saveGroups`).
function onFooterSave() {
  if (isCustomerAccount.value) {
    saveMcsAssignments();
  } else {
    saveAssignments();
  }
}

// --- MCS branch handlers ---

async function fetchSites() {
  try {
    const data = await adminApi.listSites();
    sites.value = Array.isArray(data) ? data.filter((s) => s.is_active) : [];
  } catch (err) {
    console.error('Failed to load sites:', err);
    sites.value = [];
  }
}

async function fetchMcsAvailable() {
  mcsLoadingList.value = true;
  try {
    const res = await adminApi.listMcsCustomers({
      site_id: selectedSiteChip.value,
      q: mcsSearchQuery.value || null,
      limit: 50,
      offset: 0,
    });
    mcsAvailableResults.value = Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    console.error('Failed to load MCS customers:', err);
    mcsError.value = err.message || 'Failed to load customers';
    mcsAvailableResults.value = [];
  } finally {
    mcsLoadingList.value = false;
  }
}

function setSiteChip(siteId) {
  selectedSiteChip.value = siteId;
  fetchMcsAvailable();
}

function isAssigned(id) {
  return mcsAssigned.value.some((c) => c.mcs_customer_id === id);
}

function addMcsAssignment(listRow) {
  if (isAssigned(listRow.id)) return;
  mcsAssigned.value.push({
    mcs_customer_id: listRow.id,
    site: listRow.site_name,
    company_id: listRow.company_id,
    customer_no: listRow.customer_no,
    customer_name: listRow.customer_name,
  });
}

function removeMcsAssignment(c) {
  mcsAssigned.value = mcsAssigned.value.filter(
    (x) => x.mcs_customer_id !== c.mcs_customer_id
  );
}

function handleManualAdded(created) {
  showManualAdd.value = false;
  if (!created) return;
  const normalized = {
    mcs_customer_id: created.id,
    site: created.site_name,
    company_id: created.company_id,
    customer_no: created.customer_no,
    customer_name: created.customer_name,
  };
  if (!isAssigned(normalized.mcs_customer_id)) {
    mcsAssigned.value.push(normalized);
  }
  // Refresh available list in case this site's catalog now shows it
  fetchMcsAvailable();
}

const mcsAssignedGrouped = computed(() => {
  const groups = new Map();
  for (const c of mcsAssigned.value) {
    const key = c.site || '—';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  const sorted = Array.from(groups.entries())
    .map(([site, items]) => ({
      site,
      items: [...items].sort((a, b) => (a.customer_no ?? 0) - (b.customer_no ?? 0)),
    }))
    .sort((a, b) => a.site.localeCompare(b.site));
  return sorted;
});

async function saveMcsAssignments() {
  if (!props.user) return;
  mcsError.value = null;
  mcsSaving.value = true;
  const uid = props.user.id || props.user.uid;
  const finalIds = mcsAssigned.value.map((c) => c.mcs_customer_id);
  try {
    const res = await adminApi.updateUserMcsCustomers(uid, finalIds);
    const message = 'Assignments saved.';
    emit('saved', {
      uid,
      mcs_customer_ids: finalIds,
      response: res,
      message,
    });
    // Parent will typically close us on @saved; if not, reset the initial snapshot
    initialAssignedIds = finalIds.slice();
  } catch (err) {
    console.error('Save MCS assignments failed:', err);
    mcsError.value = err.message || 'Failed to save assignments';
  } finally {
    mcsSaving.value = false;
  }
}

// Expose methods for parent component
const setLoading = (value) => {
  loading.value = value;
};

const setLoadingItems = (value) => {
  loadingItems.value = value;
};

const setError = (message) => {
  error.value = message;
};

// MCS-specific exposed helpers
const setMcsSaving = (value) => {
  mcsSaving.value = value;
};
const setMcsError = (message) => {
  mcsError.value = message;
};

defineExpose({ setLoading, setLoadingItems, setError, setMcsSaving, setMcsError });
</script>

<style scoped>
/* Tailwind handles all styling */
</style>
